import crypto from 'crypto-js'
import store from '@/store'
import { EventBus } from './EUtils'
import { submitPracticeAnswers, studyPost, submitPlanAnswer } from './study'
import { studyUuid, text, flag } from './studyModel.mjs'

const storagePrefix = 'sk-answer-pending-v1:'
const inFlight = new Map(),
  groups = new Map()
const owner = () => text((store.getters.userInfo || {}).user_id)
const storageKey = (who) => storagePrefix + crypto.SHA256(who).toString()
function read(who) {
  try {
    const data = JSON.parse(localStorage.getItem(storageKey(who)) || '[]')
    if (!Array.isArray(data)) throw new Error()
    return data
  } catch (_) {
    throw new Error('无法读取待同步答案，请检查浏览器本地存储')
  }
}
function save(who, entries) {
  try {
    localStorage.setItem(storageKey(who), JSON.stringify(entries))
  } catch (_) {
    throw new Error('无法保存待同步答案，尚未提交，请释放本地存储后重试')
  }
  EventBus.$emit('study-pending-changed')
}
export const pendingAnswers = (parent) => {
  const who = owner()
  return who ? read(who).filter((item) => !parent || text(item.parent) === text(parent)) : []
}
async function sendEntry(who, entry, source) {
  if (inFlight.has(entry.id)) return inFlight.get(entry.id)
  const task = (async () => {
    if (owner() !== who) throw new Error('账号已切换，已保留原账号待同步答案')
    let response
    try {
      response =
        entry.kind === 'learning-task'
          ? await submitPlanAnswer(JSON.parse(entry.data.answer)[0])
          : entry.kind === 'direct'
          ? await studyPost('answer-attempts/sync', entry.data)
          : await submitPracticeAnswers(entry.data, entry.kind)
    } catch (error) {
      if (error.answerSaved && owner() === who)
        save(
          who,
          read(who).filter((item) => item.id !== entry.id)
        )
      throw error
    }
    if (
      entry.kind === 'direct' &&
      !entry.data.attempts.every((attempt) =>
        (response.receipts || []).some(
          (receipt) =>
            receipt.client_attempt_id === attempt.client_attempt_id &&
            ['accepted', 'duplicate'].includes(receipt.status)
        )
      )
    )
      throw new Error('部分作答尚未确认入账，已保留待同步记录')
    if (owner() !== who) throw new Error('账号已切换，已保留原账号待同步答案')
    save(
      who,
      read(who).filter((item) => item.id !== entry.id)
    )
    EventBus.$emit('study-changed', { reason: 'answers', source })
    return response
  })()
  inFlight.set(entry.id, task)
  try {
    return await task
  } finally {
    inFlight.delete(entry.id)
  }
}
export async function submitAnswerBatch(kind, data, options = {}) {
  const who = owner()
  if (!who) throw new Error('账号资料尚未载入，请回首页后重试')
  const answers = kind === 'direct' ? data.attempts : JSON.parse(data.answer || '[]')
  if (!answers.length) return { code: 200, data: [] }
  const signature = crypto.SHA256(JSON.stringify([kind, data])).toString(),
    entries = read(who)
  let entry = entries.find((item) => item.signature === signature)
  if (!entry) {
    entry = {
      id: studyUuid(),
      signature,
      kind,
      parent: text(data.identity_parent_id || answers[0].identity_parent_id),
      created_at: new Date().toISOString(),
      data: JSON.parse(JSON.stringify(data))
    }
    entries.push(entry)
    save(who, entries)
  }
  if (kind !== 'autonomous') return sendEntry(who, entry, options.source)
  const group = who + '|' + text(data.id),
    previous = groups.get(group) || Promise.resolve()
  const task = previous
    .catch(() => {})
    .then(async () => {
      let response
      for (const older of read(who).filter(
        (item) => item.kind === kind && text(item.data.id) === text(data.id)
      )) {
        response = await sendEntry(who, older)
        if (older.id === entry.id) break
      }
      return response || { code: 200, data: [] }
    })
  groups.set(group, task)
  try {
    return await task
  } finally {
    if (groups.get(group) === task) groups.delete(group)
  }
}
export async function retryPendingAnswers(parent) {
  const who = owner()
  for (const entry of pendingAnswers(parent)) {
    if (owner() !== who) throw new Error('账号已切换，重试已停止')
    await sendEntry(who, entry)
  }
}

export function acknowledgePlanReceipts(receipts) {
  const who = owner()
  if (!who) return
  const ids = new Set(
      (receipts || [])
        .filter((receipt) => flag(receipt.answer_saved))
        .map((receipt) => text(receipt.client_attempt_id))
    ),
    entries = read(who),
    remaining = entries.filter(
      (entry) =>
        entry.kind !== 'learning-task' ||
        !JSON.parse(entry.data.answer).some((answer) => ids.has(text(answer.client_attempt_id)))
    )
  if (entries.length !== remaining.length) save(who, remaining)
}
